import "./selector.scss";
import {
    type DragEvent,
    type FC,
    Fragment,
    type MouseEvent,
    type ReactNode,
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from "react";
import {
    ActionButton,
    CancelButton,
    DialogContent,
    DialogType,
    useDialogCreate,
    useLayerRemove,
} from "@onshopfront/components";
import { useShopfrontDispatch } from "../../../_Bootstrap/hooks";
import { hideDialog, showDialog } from "../../../Actions/Dialogs/DialogActions";
import { startLoading, stopLoading } from "../../../Actions/General/LayoutActions";
import FourZeroThree from "../../../Routes/Errors/403";
import { usePermissions } from "../../../Utilities/Domain/Users/Permissions";
import { CancellablePromise } from "../../../Utilities/Generic/Promises";
import { pluralize } from "../../../Utilities/Generic/Strings";
import FontAwesomeLight from "../../../Utilities/Platform/FontAwesome/FontAwesomeLight";
import Request from "../../../Utilities/Platform/Request";
import { withPortal } from "../../../Utilities/UI/PortalHelper";
import Button, { CancelOptionButton } from "../../Buttons/Button";
import PopupManager from "../../Popups/PopupManager";
import { MediaDetails } from "./Details/MediaDetails";
import { type FileOrderType, FileViewer, SORT_OPTIONS } from "./FileViewer";
import { FolderBrowser } from "./FolderBrowser";
import { FolderCreateDialog } from "./FolderCreateDialog";
import { FolderCreateSmall } from "./FolderCreateSmall";
import { MediaSelectorOverlay } from "./MediaSelectorOverlay";
import {
    createDirectory,
    getFile,
    getMediaCentre,
    getPageIndex,
    loadDirectory,
    loadFolder,
    loadItemsPage,
    MEDIA_FILE_PAGE_SIZE,
    type MediaDirectoryType,
    type MediaDragType,
    type MediaFilePages,
    type MediaFileType,
    MediaItemType,
    type MediaPathType,
    type MediaSelectedType,
    type MediaType,
    moveItems,
    setMediaCentre,
    sortItems,
    uploadFileList,
    uploadURL,
} from "./MediaUtilities";
import { RenameItem } from "./RenameItem";

export interface MediaItem {
    url: string;
    name: string;
}

export interface MediaSelectorProps {
    small?: boolean;
    fullscreen?: boolean;
    onClose?: (event: MouseEvent) => void;
    onChange?: (value: Array<MediaItem>) => void;
    onConfigure?: (event: MouseEvent) => void;
    disableSelect?: boolean;
    filters?: Array<MediaType>;
    multi?: boolean;
}

interface IndividualFolderType {
    open: boolean;
    children: boolean | FolderListType | null;
    name: string;
}

export type FolderListType = Record<string, IndividualFolderType>;

type UploadProgress = number | false;
type UploadProgressMap = Record<string, [ UploadProgress, string ]>;

interface UploadType {
    id: string;
    progress: UploadProgress;
    name: string;
}

const permissions = [ "see_media_centre", "upload_media_content" ] as const;

export const MediaSelector: FC<MediaSelectorProps> = ({
    small,
    fullscreen,
    filters,
    onConfigure,
    onChange,
    onClose,
    disableSelect,
    multi,
}) => {
    const { seeMediaCentre, uploadMediaContent } = usePermissions(permissions);

    const dispatch = useShopfrontDispatch();
    const createDialog = useDialogCreate();
    const removeLayer = useLayerRemove();

    const requestMap = useMemo(() => {
        return new Map<string, UploadType>();
    }, []);

    const [ overlayVisible, setOverlayVisible ] = useState(false);
    const [ overlayDragging, setOverlayDragging ] = useState(false);
    const [ overlayUploading, setOverlayUploading ] = useState(false);

    const [ createFolder, setCreateFolder ] = useState(false);
    const [ folders, setFolders ] = useState<FolderListType>(() => {
        return getMediaCentre("folders", {
            home: {
                open    : false,
                children: false,
                name    : "Home",
            },
            mdb: {
                open    : false,
                children: false,
                name    : "MDB",
            },
        });
    });

    const [ current, setCurrent ] = useState<Array<MediaPathType>>(() => {
        return getMediaCentre("current", [{
            id  : "home",
            name: "Home",
        }]);
    });

    const [ uploadProgress, setUploadProgress ] = useState<UploadProgressMap>({});

    const loadingMap = useRef<Record<string, CancellablePromise<Array<MediaFileType>>>>({});

    const [ loading, setLoading ] = useState(true);
    const [ selected, setSelected ] = useState<Array<MediaSelectedType>>([]);
    const [ search, setSearch ] = useState(() => getMediaCentre("search", ""));
    const [ order, setOrder ] = useState<FileOrderType>(() => {
        return getMediaCentre("order", {
            dir  : "ASC",
            value: SORT_OPTIONS[0],
        });
    });

    const [ files, setFiles ] = useState<MediaFilePages>([]);
    const [ directory, setDirectory ] = useState<null | MediaDirectoryType>(null);
    const [ dragging, setDragging ] = useState<null | MediaDragType>(null);

    const loadDirectoryData = useCallback(() => {
        setLoading(true);
        setSelected([]);

        const keys = Object.keys(loadingMap.current);

        for(const key of keys) {
            if(key in loadingMap.current) {
                loadingMap.current[key].cancel();
            }
        }

        setFolders(prevState => {
            if(!current.length) {
                return prevState;
            }

            const newState = { ...prevState };

            let layer: IndividualFolderType = {
                ...newState[current[0].id],
                open: true,
            };

            newState[current[0].id] = layer;

            for(let i = 1, l = current.length; i < l; i++) {
                // The path isn't complete
                if(layer.children === null || typeof layer.children === "boolean") {
                    return prevState;
                }

                const newLayer = {
                    ...layer.children[current[i].id],
                    open: true,
                };

                layer.children = {
                    ...layer.children,
                    [current[i].id]: newLayer,
                };

                layer = newLayer;
            }

            if(layer.children === false) {
                layer.children = true;
            }

            return newState;
        });

        return loadDirectory(current, search, order, filters)
            .then(({ folder, files }) => {
                // Prepare the file pages of the directory
                const filePages: MediaFilePages = [ files ];
                const pageCount = Math.ceil(folder.fileCount / MEDIA_FILE_PAGE_SIZE);

                for(let i = 1; i < pageCount; i++) {
                    filePages[i] = null;
                }

                finalizeFolderLoad(current, folder);

                setDirectory(folder);
                setFiles(filePages);
                setLoading(false);
            });
    }, [ current, search, order, filters ]);

    useEffect(() => {
        setMediaCentre("folders", folders);
        setMediaCentre("current", current);
        setMediaCentre("search", search);
        setMediaCentre("order", order);
    }, [ current, search, order, folders ]);

    useEffect(() => {
        void loadDirectoryData();
    }, [ loadDirectoryData ]);

    const loadPage = useCallback(async (page: number): Promise<void> => {
        if(typeof loadingMap.current[page] !== "undefined") {
            return;
        }

        const promise = new CancellablePromise(
            loadItemsPage(
                current,
                search,
                order,
                filters,
                page
            )
        );

        loadingMap.current[page] = promise;

        promise
            .then(files => {
                delete loadingMap.current[page];

                setFiles(prevState => ([
                    ...prevState.slice(0, page),
                    files,
                    ...prevState.slice(page + 1),
                ]));
            });
    }, [ current, search, order, filters ]);

    const onFileSelect = (values: Array<MediaSelectedType>) => {
        if(typeof onChange !== "function") {
            return;
        }

        const results: Array<MediaItem> = [];

        for(const value of values) {
            const item = getFile(value.index, files);

            if(!item) {
                continue;
            }

            results.push({
                url : item.url,
                name: item.name,
            });

            // Only process the first item if multi-select disabled
            if(!multi) {
                break;
            }
        }

        if(!results.length) {
            return;
        }

        return onChange(results);
    };

    if(!seeMediaCentre) {
        return (
            <div className="media-denied">
                <FourZeroThree
                    permissions={[]}
                />
            </div>
        );
    }

    const className = [ "media-selector" ];

    if(small) {
        className.push("small");
    } else if(fullscreen) {
        className.push("fullscreen");
    }

    const showUploadProgress = (id: string, name: string, event: ProgressEvent | true): void => {
        let map = requestMap.get(id);

        if(!map) {
            map = {
                id,
                name,
                progress: false,
            };

            requestMap.set(id, map);
        }

        if(event === true) {
            setUploadProgress(prevState => {
                const newState = { ...prevState };

                delete newState[id];

                return newState;
            });

            return;
        }

        map.progress = !event.lengthComputable ?
            false :
            Math.round(event.loaded / event.total * 100);

        setUploadProgress(prevState => {
            if(!map) {
                return prevState;
            }

            return {
                ...prevState,
                [map.id]: [ map.progress, map.name ],
            };
        });
    };

    const onUpload = async (upload: FileList | string): Promise<void> => {
        if(current?.[0]?.id !== "home" || !uploadMediaContent) {
            PopupManager.error("There was an issue uploading file. Permission Denied");

            return;
        }

        // Upload URL
        let promise: Promise<Array<MediaFileType>>;

        if(typeof upload === "string") {
            promise = uploadURL(current, upload);
        } else {
            promise = uploadFileList(current, upload, showUploadProgress);
        }

        setOverlayUploading(true);

        return promise
            .then(() => {
                if(!filters?.length) {
                    PopupManager.success("Upload Successful");
                } else {
                    let filters = "";

                    for(let i = 0, l = filters.length; i < l; i++) {
                        const filter = pluralize(filters[i].toLowerCase(), 2);

                        if(i === l - 2) {
                            filters += `and ${filter}`;
                        }

                        if(i === 0) {
                            filters += filter;
                        } else {
                            filters += `, ${filter}`;
                        }
                    }

                    PopupManager.success(`Upload Successful, only showing ${filters}`);
                }

                loadDirectoryData();
            })
            .catch(err => {
                let errorMessage = "There was an issue uploading file.";

                if(typeof err === "string") {
                    errorMessage += ` ${err}.`;
                }

                PopupManager.error(errorMessage);
            })
            .then(() => {
                setOverlayUploading(false);
            });
    };

    const onUploadClick = (e: MouseEvent): void => {
        e.preventDefault();

        const input = document.createElement("input");

        input.type = "file";
        input.multiple = true;

        input.onchange = (): void => {
            if(input.files === null) {
                return;
            }

            void onUpload(input.files);
        };

        input.click();
    };

    const finalizeDirectoryCreate = async (name: string): Promise<void> => {
        if(!name) {
            PopupManager.warning("Directory name required");

            return;
        }

        if(
            name === "..." ||
            name === ".." ||
            name === "." ||
            name === "new-folder"
        ) {
            PopupManager.warning(`Invalid directory name, "${name}"`);

            return;
        }

        if(name.charAt(0) === "_") {
            PopupManager.warning(`Invalid directory name, "${name}", name must start with an alphanumeric`);

            return;
        }

        if(!name.match(/^[a-zA-Z0-9()_\-.\x20]*$/)) {
            PopupManager.warning(`Invalid directory name, "${name}", name has invalid characters`);

            return;
        }

        dispatch(startLoading());

        return createDirectory(current, name)
            .then(({ uuid, name }) => {
                PopupManager.success("Successfully created directory");

                setDirectory(prevState => {
                    if(!prevState) {
                        return null;
                    }

                    const folderId = current.length === 1 ? null : current[current.length - 1].id;

                    return {
                        ...prevState,
                        folders: sortItems([
                            ...prevState.folders,
                            {
                                uuid,
                                name,
                                folderId,
                                count    : 0,
                                createdAt: new Date().toISOString(),
                                updatedAt: null,
                            },
                        ], order),
                    };
                });

                // Add the new folder in the directory browser
                setFolders(prevState => {
                    if(!current.length) {
                        return prevState;
                    }

                    const newState = { ...prevState };

                    let layer: IndividualFolderType = {
                        ...newState[current[0].id],
                    };

                    newState[current[0].id] = layer;

                    for(let i = 1, l = current.length; i < l; i++) {
                        // The path isn't complete
                        if(layer.children === null || typeof layer.children === "boolean") {
                            return prevState;
                        }

                        const newLayer = {
                            ...layer.children[current[i].id],
                        };

                        layer.children = {
                            ...layer.children,
                            [current[i].id]: newLayer,
                        };

                        layer = newLayer;
                    }

                    // The path isn't complete
                    if(typeof layer.children === "boolean") {
                        startLoadFolder(current);

                        return prevState;
                    }

                    layer.children = {
                        ...(layer.children === null ? {} : layer.children),
                        [uuid]: {
                            open    : true,
                            children: null,
                            name,
                        },
                    };

                    return newState;
                });
            })
            .catch(err => {
                console.error("There was an issue trying to create the directory", err);
                PopupManager.error("There was an issue trying to create the directory");
            })
            .then(() => {
                dispatch(stopLoading());
            });
    };

    const onDirectoryCreate = (e: MouseEvent): void => {
        if(small) {
            setCreateFolder(true);

            return;
        }

        e.stopPropagation();

        dispatch(showDialog(
            "Create New Directory",
            <FolderCreateDialog onNew={finalizeDirectoryCreate} />,
            "question",
            false,
            false
        ));
    };

    const finalizeFolderLoad = (path: Array<MediaPathType>, directory: MediaDirectoryType): void => {
        setFolders(prevState => {
            if(!path.length) {
                return prevState;
            }

            const newState: FolderListType = { ...prevState };

            let layer: IndividualFolderType = {
                ...newState[path[0].id],
            };

            newState[path[0].id] = layer;

            for(let i = 1, l = path.length; i < l; i++) {
                // The path isn't complete and the previous folders need to be loaded
                if(layer.children === null || typeof layer.children === "boolean") {
                    return prevState;
                }

                const newLayer = { ...layer.children[path[i].id] };

                layer.children = {
                    ...layer.children,
                    [path[i].id]: newLayer,
                };

                layer = newLayer;
            }

            if(!directory.folders.length) {
                layer.children = null;
            } else {
                const toRemove = new Set<string>();

                // Prepare to merge the folders
                let children: FolderListType = {};

                if(!(layer.children === null || typeof layer.children === "boolean")) {
                    children = { ...layer.children as FolderListType };

                    for(const id in children) {
                        toRemove.add(id);
                    }
                }

                for(let i = 0, l = directory.folders.length; i < l; i++) {
                    if(directory.folders[i].uuid in children) {
                        children[directory.folders[i].uuid] = {
                            ...children[directory.folders[i].uuid],
                            name: directory.folders[i].name,
                        };
                    } else {
                        children[directory.folders[i].uuid] = {
                            open    : false,
                            children: false,
                            name    : directory.folders[i].name,
                        };
                    }

                    // The file still exists in the directory
                    toRemove.delete(directory.folders[i].uuid);
                }

                for(const id of toRemove) {
                    if(id in children) {
                        delete children[id];
                    }
                }

                layer.children = children;
            }

            return newState;
        });
    };

    const startLoadFolder = (path: Array<MediaPathType>): void => {
        loadFolder(path, "", filters)
            .then(folder => {
                finalizeFolderLoad(path, folder);
            });
    };

    const finalizeToggle = (
        object: FolderListType,
        position: Array<MediaPathType>,
        path: Array<MediaPathType>
    ): FolderListType => {
        const pointer = position.shift();

        if(typeof pointer === "undefined") {
            return object;
        }

        const id = pointer.id;

        if(id in object) {
            if(position.length) {
                if(typeof object[id].children !== "object" || object[id].children === null) {
                    return object;
                }

                object[id] = {
                    ...object[id],
                    children: finalizeToggle(
                        object[id].children as FolderListType,
                        position,
                        path
                    ),
                };

                return object;
            } else {
                let val = object[id].open;

                if(typeof val === "undefined") {
                    val = false;
                }

                const newObject = {
                    ...object[id],
                };

                newObject.open = !val;

                if(newObject.children === false) {
                    newObject.children = true;

                    startLoadFolder(path);
                }

                object[id] = newObject;

                return object;
            }
        }

        return object;
    };

    const onToggle = (value: Array<MediaPathType>): void => {
        setFolders(prevState => finalizeToggle(
            { ...prevState },
            [ ...value ],
            value
        ));
    };

    const onDragEnter = (e: DragEvent): void => {
        if(current?.[0]?.id !== "home") {
            return;
        }

        // Dragging a tile around the grid isn't an upload, so the upload overlay must stay out of the way
        if(dragging) {
            return;
        }

        e.preventDefault();
        e.stopPropagation();

        setOverlayVisible(true);
        setOverlayDragging(true);
        setOverlayUploading(false);
    };

    const onDragStart = (item: MediaSelectedType): void => {
        // Dragging something outside the current selection only moves that item, matching how most file
        // browsers behave. Selection never mixes types, but the filter keeps the payload honest regardless.
        const inSelection = selected.some(value => value.type === item.type && value.id === item.id);

        setDragging({
            type : item.type,
            items: inSelection ? selected.filter(value => value.type === item.type) : [ item ],
        });
    };

    const onDragEnd = (): void => {
        setDragging(null);
    };

    const finalizeMove = async (target: MediaPathType, drag: MediaDragType): Promise<void> => {
        dispatch(startLoading());

        const { moved, failed } = await moveItems(
            drag.items,
            target.id === "home" ? null : target.id
        );

        if(failed.length) {
            PopupManager.error(`Failed to move ${failed.length} ${pluralize(drag.type, failed.length)}`);
        }

        if(moved.length) {
            PopupManager.success(
                `Successfully moved ${moved.length} ${pluralize(drag.type, moved.length)} to ${target.name}`
            );
        }

        // The server renames anything that would have clashed with a name already in the destination
        const renamed = moved.filter(result => result.name !== result.item.name);

        if(renamed.length === 1) {
            PopupManager.info(
                `"${renamed[0].item.name}" was renamed to "${renamed[0].name}" as that name was already in use`
            );
        } else if(renamed.length > 1) {
            PopupManager.info(`${renamed.length} items were renamed as their names were already in use`);
        }

        await loadDirectoryData();

        dispatch(stopLoading());
    };

    const onMove = (target: MediaPathType, drag: MediaDragType): void => {
        setDragging(null);

        if(current?.[0]?.id !== "home" || !drag.items.length) {
            return;
        }

        const count = drag.items.length;

        const description = count === 1 ?
            `"${drag.items[0].name}"` :
            `${count} ${pluralize(drag.type, count)}`;

        const dialogId = createDialog({
            type   : DialogType.Question,
            title  : "Move Items",
            content: (
                <DialogContent>
                    <p>Are you sure you want to move {description} to {target.name}?</p>
                </DialogContent>
            ),
            buttons: [
                <CancelButton onClick={() => removeLayer(dialogId)} key="cancel" />,
                <ActionButton
                    onClick={() => {
                        removeLayer(dialogId);
                        void finalizeMove(target, drag);
                    }}
                    key="move"
                >
                    Move
                </ActionButton>,
            ],
        });
    };

    const deleteFolder = async (item: MediaSelectedType): Promise<void> => {
        dispatch(hideDialog());
        dispatch(startLoading());

        try {
            await Request.delete(`/media/${item.id}/folder`);

            PopupManager.info("Successfully deleted folder");

            setSelected([]);

            setDirectory(prevState => {
                if(!prevState) {
                    return prevState;
                }

                return {
                    ...prevState,
                    folders: [
                        ...prevState.folders.slice(0, item.index),
                        ...prevState.folders.slice(item.index + 1),
                    ],
                };
            });

            setFolders(prevState => {
                if(!current.length) {
                    return prevState;
                }

                const newState = { ...prevState };

                let layer: IndividualFolderType = {
                    ...newState[current[0].id],
                    open: true,
                };

                newState[current[0].id] = layer;

                for(let i = 1, l = current.length; i < l; i++) {
                    // The path isn't complete
                    if(layer.children === null || typeof layer.children === "boolean") {
                        return prevState;
                    }

                    const newLayer = {
                        ...layer.children[current[i].id],
                        open: true,
                    };

                    layer.children = {
                        ...layer.children,
                        [current[i].id]: newLayer,
                    };

                    layer = newLayer;
                }

                // The path isn't complete
                if(layer.children === null || typeof layer.children === "boolean") {
                    return prevState;
                }

                const { [item.id]: _, ...rest } = layer.children;

                if(Object.keys(rest).length) {
                    layer.children = rest;
                } else {
                    layer.children = null;
                }

                return newState;
            });
        } catch(err) {
            console.error("There was an issue trying to delete the item", err);
            PopupManager.error("There was an issue trying to delete the item");
        }

        dispatch(stopLoading());
    };

    const deleteFiles = async (selectedFiles: Array<MediaSelectedType>): Promise<void> => {
        if(!files.length) {
            return;
        }

        dispatch(hideDialog());
        dispatch(startLoading());

        let errors = 0;

        for(const selected of selectedFiles) {
            if(selected.type !== MediaItemType.File) {
                continue;
            }

            const file = getFile(selected.index, files);

            if(!file) {
                continue;
            }

            if(file.connections.length) {
                continue;
            }

            try {
                await Request.delete(`/media/${selected.id}/file`);
            } catch(err) {
                console.error("There was an issue trying to delete the file", err);
                errors++;
            }
        }

        if(errors > 0) {
            PopupManager.error(`Failed to delete ${errors} ${pluralize("file", errors)}`);
        } else {
            PopupManager.info(`Successfully deleted ${pluralize("file", files.length)}`);
        }

        // This should eventually be replaced with a better system
        void loadDirectoryData();

        dispatch(stopLoading());
    };

    const finalizeDelete = async (): Promise<void> => {
        if(selected.length === 0) {
            return;
        }

        if(selected.length === 1 && selected[0].type === MediaItemType.Folder) {
            await deleteFolder(selected[0]);
        } else {
            await deleteFiles(selected);
        }
    };

    const onDeleteClick = (e: MouseEvent): void => {
        if(selected.length === 0 || !directory) {
            return;
        }

        // Create options
        const options = [ CancelOptionButton, {
            label   : "Delete",
            classes : [ "button-danger" ],
            callback: finalizeDelete,
        }];

        let type = "";
        let name = "";
        let line = null;

        let content: ReactNode;

        if(selected.length === 1) {
            if(selected[0].type === MediaItemType.Folder) {
                const item = directory.folders[selected[0].index];

                type = "folder";
                name = item.name;

                if(item.count) {
                    line = (
                        <p>Warning this directory contains {item.count} item{item.count > 1 ? "s" : ""}!</p>
                    );
                }
            } else if(selected[0].type === MediaItemType.File) {
                const item = getFile(selected[0].index, files);

                if(!item) {
                    return;
                }

                type = "file";
                name = item.name;
            }

            content = (
                <div>
                    <p>Are you sure you want to delete this {type}?</p>
                    {line}
                    <p>This cannot be undone</p>
                </div>
            );
        } else {
            name = "Files";

            let inUseCount = 0;
            let deletableCount = 0;

            for(let i = 0, l = selected.length; i < l; i++) {
                const file = getFile(selected[i].index, files);

                if(file?.connections.length) {
                    inUseCount++;
                } else {
                    deletableCount++;
                }
            }

            if(deletableCount === 0) {
                return;
            }

            content = (
                <div className="max-w-md">
                    {inUseCount === 0 ? (
                        <p>Are you sure you want to delete {deletableCount} files?</p>
                    ) : (
                        <>
                            {inUseCount === 1 && (
                                <p>There is a file that is in use in Shopfront and won&apos;t be deleted.</p>
                            )}
                            {inUseCount > 1 && (
                                <p>
                                    There are {inUseCount} files that are in use in Shopfront and won&apos;t be deleted.
                                </p>
                            )}
                            <p>
                                Are you sure you want to delete the
                                remaining {deletableCount} {pluralize("file", deletableCount)}?
                            </p>
                        </>
                    )}
                    <p>This cannot be undone</p>
                </div>
            );
        }

        // Show form
        dispatch(showDialog(
            `Delete ${name}`,
            content,
            "danger",
            options
        ));

        // Prevent the flow up to the body
        e.stopPropagation();
    };

    const onRenameClick = (e: MouseEvent): void => {
        if(selected.length !== 1 || !directory) {
            return;
        }

        const selectedItem = selected[0];

        // Show form
        dispatch(showDialog(
            `Rename ${selectedItem.type}`,
            <RenameItem
                path={current}
                item={selectedItem}
                onRename={(name) => {
                    if(selectedItem.type === MediaItemType.Folder) {
                        // Rename the folders
                        setDirectory(prevState => {
                            if(!prevState || !selected) {
                                return prevState;
                            }

                            return {
                                ...prevState,
                                folders: [
                                    ...prevState.folders.slice(0, selectedItem.index),
                                    {
                                        ...prevState.folders[selectedItem.index],
                                        name,
                                        updatedAt: new Date().toISOString(),
                                    },
                                    ...prevState.folders.slice(selectedItem.index + 1),
                                ],
                            };
                        });

                        setFolders(prevState => {
                            if(!current.length) {
                                return prevState;
                            }

                            const newState = { ...prevState };

                            let layer: IndividualFolderType = {
                                ...newState[current[0].id],
                            };

                            newState[current[0].id] = layer;

                            for(let i = 1, l = current.length; i < l; i++) {
                                // The path isn't complete
                                if(layer.children === null || typeof layer.children === "boolean") {
                                    return prevState;
                                }

                                const newLayer = {
                                    ...layer.children[current[i].id],
                                };

                                layer.children = {
                                    ...layer.children,
                                    [current[i].id]: newLayer,
                                };

                                layer = newLayer;
                            }

                            if(layer.children === null || typeof layer.children === "boolean") {
                                return prevState;
                            }

                            if(typeof layer.children[selectedItem.id] === "undefined") {
                                return prevState;
                            }

                            layer.children[selectedItem.id].name = name;

                            return newState;
                        });
                    } else if(selectedItem.type === MediaItemType.File) {
                        // Rename the file
                        setFiles(prevState => {
                            if(!selected) {
                                return prevState;
                            }

                            const pageNumber = getPageIndex(selectedItem.index);
                            const pageIndex = selectedItem.index % MEDIA_FILE_PAGE_SIZE;

                            const page = prevState[pageNumber];

                            return [
                                ...prevState.slice(0, pageNumber),
                                page === null ? null : [
                                    ...page.slice(0, pageIndex),
                                    {
                                        ...page[pageIndex],
                                        name,
                                        updatedAt: new Date().toISOString(),
                                    },
                                    ...page.slice(pageIndex + 1),
                                ],
                                ...prevState.slice(pageNumber + 1),
                            ];
                        });
                    }
                }}
            />,
            "question",
            false,
            false
        ));

        // Prevent the flow up to the body
        e.stopPropagation();
    };

    const canUpload = current?.[0]?.id === "home";

    let closeButton = null;

    if(typeof onClose === "function") {
        closeButton = (
            <div className="close-button" onClick={onClose}>
                <FontAwesomeLight icon="times" />
            </div>
        );
    }

    let content: ReactNode;

    if(small) {
        let body: ReactNode;

        if(createFolder) {
            body = (
                <FolderCreateSmall
                    path={current}
                    onNew={finalizeDirectoryCreate}
                    onCancel={() => {
                        setCreateFolder(false);
                    }}
                />
            );
        } else {
            body = (
                <Fragment>
                    <FileViewer
                        small={small}
                        current={current}
                        loading={loading}
                        directory={directory}
                        items={files}
                        onLoadMore={loadPage}
                        onDirChange={setCurrent}
                        onRefresh={loadDirectoryData}
                        onSelect={onFileSelect}
                        onChange={setSelected}
                        selected={selected}
                        onSearch={setSearch}
                        search={search}
                        order={order}
                        onReorder={setOrder}
                        onNewFolder={onDirectoryCreate}
                        multi={!!multi}
                        dragging={dragging}
                        onDragStart={onDragStart}
                        onDragEnd={onDragEnd}
                        onMove={onMove}
                    />
                    <MediaDetails
                        small={small}
                        selected={selected}
                        directory={directory}
                        pages={files}
                        actions={canUpload}
                        disableSelect={!!disableSelect}
                        multi={!!multi}
                        onDelete={onDeleteClick}
                        onRename={onRenameClick}
                        onClick={() => {
                            onFileSelect(selected);
                        }}
                    />
                </Fragment>
            );
        }

        content = (
            <div className="media-grid" onDragEnter={onDragEnter}>
                <div className="media-small-buttons">
                    <div className="upload-button">
                        <Button
                            onClick={onUploadClick}
                            disabled={!(
                                canUpload &&
                                uploadMediaContent
                            )}
                        >
                            <FontAwesomeLight icon="cloud-upload" />Upload
                        </Button>
                    </div>
                    <div className="configure-button">
                        <Button onClick={onConfigure}>
                            <FontAwesomeLight icon={"cog"} />
                        </Button>
                    </div>
                </div>
                {body}
            </div>
        );
    } else {
        let overlay: ReactNode = null;

        if(canUpload) {
            overlay = (
                <MediaSelectorOverlay
                    visible={overlayVisible}
                    onVisible={setOverlayVisible}
                    dragging={overlayDragging}
                    onDragging={setOverlayDragging}
                    uploading={overlayUploading}
                    onUploading={setOverlayUploading}
                    canUpload={uploadMediaContent}
                    onUpload={onUpload}
                />
            );
        }

        content = (
            <div className="media-grid" onDragEnter={onDragEnter}>
                <FolderBrowser
                    path={current}
                    folders={folders}
                    onNewFolder={onDirectoryCreate}
                    onToggle={onToggle}
                    onChange={setCurrent}
                    onUpload={onUploadClick}
                    onOverlayDisplay={setOverlayVisible}
                />
                <FileViewer
                    small={small}
                    current={current}
                    loading={loading}
                    directory={directory}
                    items={files}
                    onLoadMore={loadPage}
                    onDirChange={setCurrent}
                    onRefresh={loadDirectoryData}
                    onSelect={onFileSelect}
                    onChange={setSelected}
                    selected={selected}
                    onSearch={setSearch}
                    search={search}
                    order={order}
                    onReorder={setOrder}
                    onNewFolder={onDirectoryCreate}
                    multi={!!multi}
                    dragging={dragging}
                    onDragStart={onDragStart}
                    onDragEnd={onDragEnd}
                    onMove={onMove}
                />
                <MediaDetails
                    small={small}
                    selected={selected}
                    directory={directory}
                    pages={files}
                    actions={canUpload}
                    disableSelect={!!disableSelect}
                    multi={!!multi}
                    onDelete={onDeleteClick}
                    onRename={onRenameClick}
                    onClick={() => {
                        onFileSelect(selected);
                    }}
                />
                {overlay}
            </div>
        );
    }

    const uploads = Object.keys(uploadProgress);

    return (
        <div className={className.join(" ")}>
            {closeButton}
            {content}
            {uploads.length > 0 && (
                <div className="media-upload-progress">
                    {uploads.map(upload => (
                        <div key={upload} className="media-upload-progress-item">
                            <div className="media-upload-progress-image">
                                <FontAwesomeLight icon="spinner-third" spin />
                            </div>
                            <div className="media-upload-progress-name">
                                {uploadProgress[upload][1]}
                            </div>
                            <div className="media-upload-progress-bar">
                                {uploadProgress[upload][0] === false ? (
                                    <div className="media-upload-progress-bar-full" />
                                ) : (
                                    <div
                                        className="media-upload-progress-bar-part"
                                        style={{ width: `${uploadProgress[upload][0] as number}%` }}
                                    />
                                )}
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

export default withPortal("#media-frame", MediaSelector);
